import { useRef, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import styles from "./styles.module.scss";
import PrimaryButton from "../../../../../components/common/PrimaryButton";
import Spinner from "../../../../../components/common/Spinner";
import { matchLockerShipments } from "../../../../../setup/api/services/lockerShipment";
import { IMatchLockerShipmentsData } from "../../../../../setup/api/services/lockerShipment/models";
import MatchedShipmentsModal from "./MatchedShipmentsModal";
import { getApiErrorMessage } from "./helpers";

const ACCEPTED_EXTENSIONS = [".xlsx", ".csv"];

/**
 * Settles locker shipment fees owed to a service provider (e.g. Redbox): the
 * admin uploads the provider's sheet (.xlsx or .csv), previews which contracts matched
 * its tracking numbers, then settles all matched contracts at once.
 */
const LockerShipmentSettlement = () => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [matching, setMatching] = useState(false);
  const [matchResult, setMatchResult] = useState<IMatchLockerShipmentsData>();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset so re-selecting the same file (e.g. after a failed upload) fires onChange again.
    e.target.value = "";
    if (!file) return;

    if (
      !ACCEPTED_EXTENSIONS.some((ext) => file.name.toLowerCase().endsWith(ext))
    ) {
      toast.error(
        `صيغة الملف غير مدعومة. ${t("lbl_accepted_formats_csv_xlsx")}`
      );
      return;
    }

    setMatching(true);
    try {
      const response = await matchLockerShipments(file);
      setMatchResult({
        matchedContracts: response.data?.matchedContracts || [],
        unmatchedTrackingNumbers: response.data?.unmatchedTrackingNumbers || [],
      });
    } catch (error) {
      toast.error(
        getApiErrorMessage(error, "تعذر قراءة الملف أو مطابقة الشحنات")
      );
    } finally {
      setMatching(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerContainer}>
        <div>
          <h2 className={styles.title}>تسوية رسوم الشحن</h2>
          <p className={styles.subtitle}>
            ارفع الملف المرسل من مزود الخدمة (يحتوي على عمود &quot;Tracking
            number&quot;) لمطابقة الشحنات مع المعاملات المستحقة لرسوم الشحن، ثم
            قم بتسوية المعاملات المطابقة.
          </p>
        </div>
        <PrimaryButton
          width="200px"
          disabled={matching}
          onClick={() => inputRef.current?.click()}>
          {matching ? <Spinner /> : "رفع الملف"}
        </PrimaryButton>
      </div>
      <p className={styles.hint}>{t("lbl_accepted_formats_csv_xlsx")}</p>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_EXTENSIONS.join(",")}
        hidden
        onChange={handleFileChange}
      />

      <MatchedShipmentsModal
        result={matchResult}
        closeModal={() => setMatchResult(undefined)}
        onUploadAnother={() => inputRef.current?.click()}
      />
    </div>
  );
};

export default LockerShipmentSettlement;
